import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

export interface BulkImportResult {
  total: number;
  created: number;
  failed: number;
  errors: Array<{ row: number; field?: string; message: string }>;
}

export interface BulkImportDialogProps {
  /** Title shown above the file picker, e.g. "Import items". */
  title: string;
  /** Endpoint under /api/v1, e.g. "/items/bulk-import". */
  endpoint: string;
  /** Sample header line to download as a template. */
  templateCsv: string;
  /** Suggested filename for the template download. */
  templateName: string;
  /** Called after a successful upload; parent should refresh its list. */
  onDone: () => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

/**
 * Drop-in dialog that POSTs a CSV body to a bulk-import endpoint and shows
 * per-row results. Used from PartiesPage and ItemsPage. Backend returns a
 * `BulkImportResult` whether some rows fail or not, so the UI can show
 * partial success.
 */
export function BulkImportDialog({
  title,
  endpoint,
  templateCsv,
  templateName,
  onDone,
  onCancel,
}: BulkImportDialogProps): JSX.Element {
  const { t } = useTranslation('common');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const downloadTemplate = (): void => {
    const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onUpload = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const text = await file.text();
      const r = await api().post<BulkImportResult>(endpoint, text, {
        headers: { 'Content-Type': 'text/csv' },
      });
      setResult(r.data);
      if (r.data.failed === 0) onDone();
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(msg ?? t('actions.retry'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Card className="w-full max-w-xl">
        <header className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onCancel} aria-label="Close">
            ×
          </Button>
        </header>

        {!result ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-fg-muted">
              {t('bulkImport.help', {
                defaultValue: 'Upload a CSV with one row per record. Required columns are listed in the template.',
              })}
            </p>
            <Button variant="secondary" size="sm" onClick={downloadTemplate}>
              {t('bulkImport.downloadTemplate', { defaultValue: 'Download template' })}
            </Button>
            <label className="flex flex-col gap-2 text-sm">
              <span className="text-fg-muted">
                {t('bulkImport.pickFile', { defaultValue: 'Pick a CSV file' })}
              </span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full rounded-md border border-border bg-bg p-2 text-sm"
              />
            </label>
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onCancel} disabled={busy}>
                {t('actions.cancel')}
              </Button>
              <Button onClick={onUpload} disabled={!file || busy} loading={busy}>
                {t('bulkImport.upload', { defaultValue: 'Upload' })}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              {t('bulkImport.summary', {
                defaultValue: 'Created {{created}} of {{total}}. {{failed}} failed.',
                ...result,
              })}
            </p>
            {result.errors.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-start text-xs">
                  <thead className="bg-bg-muted text-fg-subtle">
                    <tr>
                      <th className="w-16 px-2 py-1 text-start">
                        {t('bulkImport.row', { defaultValue: 'Row' })}
                      </th>
                      <th className="px-2 py-1 text-start">
                        {t('bulkImport.error', { defaultValue: 'Error' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, idx) => (
                      <tr key={idx} className="border-t border-border">
                        <td className="px-2 py-1 font-mono">{err.row}</td>
                        <td className="px-2 py-1">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button onClick={onDone}>{t('actions.done', { defaultValue: 'Done' })}</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
